import accountStyle from "./account.module.css";
import formStyle from "../Common/Forms/form.module.css";
import buttonStyle from "../Common/buttons.module.css";

import { useState, useEffect, useContext, useCallback } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import CategorySelector from "./components/CategorySelector/CategorySelector.tsx";

import { Pencil, KeyRound, CircleDollarSign, CircleUser, ArrowLeft } from "lucide-react";
import ContactInformationForm from "../Common/Forms/ContactInformationForm/ContactInformationForm.tsx";
import { apiChangePassword, apiTemplateRetrieve, apiUserDetailsRetrieve } from "../../sdk.ts";
import { AuthContext } from "../../contexts/AuthContext.ts";
import { AppContext } from "../../contexts/AppContext.ts";
import axios from "axios";
import { changeContactInfo } from "../Common/Forms/changeContactInfo.ts";
import { ContactInformation } from "../../sdkStructs.ts";

interface AccountForm {
  name?: string;
  email?: string;
  password?: string;

  old_password?: string;
  new_password?: string;
  confirm_password?: string;

  picture?: string;

  Name?: string;
  Email?: string;
  PhoneNumber?: string;
  Company?: string;
  motto?: string;
  logo?: string;
}

const categories = ["Account Data", "Password & Security", "Pentest Information", "Billing & Premium"];

interface Props {
  settings: boolean;
}
function Account({ settings }: Readonly<Props>) {
  const { isAuthenticated, user } = useAuth0();
  const { setLoading } = useContext(AppContext);
  const [category, setCategory] = useState(settings ? 2 : 0);

  const [previewImage, setPreviewImage] = useState<string | null>(null);

  const [formState, setFormState] = useState<AccountForm>({});
  const [displayText, setDisplayText] = useState<React.ReactNode>();

  const token = useContext(AuthContext).token;

  const getDefault = useCallback(async () => {
    if (user === undefined) return {};

    let response, demoResponse;
    switch (category) {
      case -1: // Change profile picture
        return { picture: undefined };
      case 0: // Change username
        return { name: user.nickname, email: user.email, password: "" };
      case 1: // Change password
        return { old_password: "", new_password: "", confirm_password: "" };
      case 2: // Change contact information
        setLoading(true);
        response = await apiUserDetailsRetrieve(token);
        console.log(response.data);

        demoResponse = await apiTemplateRetrieve(token);
        console.log(demoResponse.data);
        setLoading(false);
        return { ...response.data, PhoneNumber: response.data.PhoneNumber };
      case 3: // Billing information
        return {};
      default:
        return {};
    }
  }, [category, token, user, setLoading]);

  const load = useCallback(async () => {
    if (isAuthenticated && user !== undefined) {
      const data = await getDefault();
      setFormState(data);
    }
  }, [getDefault, isAuthenticated, user]);

  useEffect(() => {
    setDisplayText(undefined);
    load();
  }, [category, load]);

  function setSuccess() {
    setDisplayText(<p>Succesfully updated account data!</p>);
  }
  function setFailure() {
    setDisplayText(<p>An error occured while updating account data. Please try again later</p>);
  }
  async function resetPasswordWithEmail() {
    try {
      setLoading(true);
      await apiChangePassword(token);
      setLoading(false);
      setDisplayText(
        <p>
          A <span className={accountStyle.blueText}>password reset</span> email was sent
        </p>,
      );
    } catch (error) {
      setLoading(false);
      setDisplayText(<p>An error has occured, email could not be sent</p>);

      if (!axios.isAxiosError(error)) {
        return;
      }
      console.log("ERROR SENDING PASSWORD RESET EMAIL:", error.response);
    }
  }

  useEffect(() => {
    setPreviewImage(null);
  }, [category]);

  if (user === undefined) {
    return;
  }

  // Functions updating changes in the auth0 database
  function changeUserData(event: React.FormEvent) {
    event.preventDefault();
    setFailure();
  }

  // Used to preview uploaded image
  const onImageChange = (event: React.FormEvent<HTMLInputElement>) => {
    if (event.currentTarget.files?.[0]) {
      setPreviewImage(URL.createObjectURL(event.currentTarget.files[0]));
    }
  };

  function getPreviewImage() {
    if (previewImage === null) return;
    return <img alt="Preview" src={previewImage} className={accountStyle.previewImage} />;
  }

  function getCategorySettings() {
    if (displayText)
      return (
        <div className={accountStyle.accountForm}>
          <div className={accountStyle.betaText + " " + accountStyle.centerText}>
            <button
              className={buttonStyle.button + " " + buttonStyle.lightBlueOutline + " " + accountStyle.resendButton}
              onClick={async () => {
                await load();
                setDisplayText(undefined);
              }}
            >
              <ArrowLeft stroke="var(--light-blue-icon)" strokeWidth="1.5" />
            </button>
            {displayText}
          </div>
        </div>
      );

    switch (category) {
      case -1:
        return (
          <form
            onSubmit={(event: React.FormEvent) => {
              event.preventDefault();
            }}
            className={accountStyle.accountForm}
          >
            <div className={formStyle.fieldsWrapper}>
              <h5 className={formStyle.firstLabel}>{"Picture"}</h5>
              <input
                className={formStyle.inputField}
                type={"file"}
                value={formState.picture}
                onChange={(e: React.FormEvent<HTMLInputElement>) => {
                  onImageChange(e);
                  setFormState({ ...formState, picture: e.currentTarget.value });
                }}
              />

              {getPreviewImage()}
            </div>
            <button type="submit" className={formStyle.saveButton + " " + accountStyle.saveButton}>
              Save
            </button>
          </form>
        );
      case 0:
        return (
          <form onSubmit={changeUserData} className={accountStyle.accountForm}>
            <div className={formStyle.fieldsWrapper}>
              <h5 className={formStyle.firstLabel}>{"Name"}</h5>
              <input readOnly className={formStyle.inputField} type={"text"} value={formState.name} />

              <h5 className={formStyle.label}>{"Email"}</h5>
              <input readOnly className={formStyle.inputField} type={"email"} value={formState.email} />
            </div>
          </form>
        );

      case 1:
        return (
          <div className={accountStyle.accountForm}>
            <p className={accountStyle.resetPasswordText}>
              If you lost your password, you can request a{" "}
              <span className={accountStyle.blueText + " " + accountStyle.noButton}>password reset email</span>:
            </p>
            <button
              onClick={resetPasswordWithEmail}
              className={
                buttonStyle.button + " " + buttonStyle.lightBlueOutline + " " + accountStyle.resetPasswordButton
              }
            >
              Reset Password
            </button>{" "}
          </div>
        );
      case 2:
        if (!formState.Name) return;

        return (
          <div className={accountStyle.accountForm}>
            <p className={accountStyle.resetPasswordText}>
              Enter your <span className={accountStyle.blueText}>contact information</span>, to be sourced as{" "}
              <span className={accountStyle.blueText}>default</span> information for every pentest
            </p>
            <ContactInformationForm
              formDefault={formState as ContactInformation}
              submit={(data: ContactInformation) => {
                setLoading(true);
                try {
                  changeContactInfo(data, token);
                  setSuccess();
                } catch (e) {
                  console.log(e);
                }
                setLoading(false);
              }}
            />
          </div>
        );
      case 3:
        return (
          <div className={accountStyle.accountForm}>
            <p className={accountStyle.betaText}>
              This software is currently in <span className={accountStyle.blueText}>beta</span>, so there are no premium
              features.
              <br />
              <br />
              However, you can help use imporve your experience by{" "}
              <span className={accountStyle.blueText}>submitting feedback!</span>
            </p>
          </div>
        );
      default:
        return <div></div>;
    }
  }
  function getIcon(id: number) {
    switch (id) {
      case 0:
        return CircleUser;
      case 1:
        return KeyRound;
      case 3:
        return CircleDollarSign;
      default:
        return Pencil;
    }
  }

  return (
    <div className={accountStyle.accountWrapper}>
      <div className={accountStyle.aboveDivider}>
        <div className={accountStyle.halfCircle}>
          <img alt="Profile" src={user.picture} className={accountStyle.profilePicture} />
          <button onClick={() => setCategory(-1)} className={accountStyle.hoverPen}>
            <Pencil className={accountStyle.pencilIcon} stroke="white" strokeWidth="1" />
          </button>
        </div>
        <span className={accountStyle.pageTitle}>My Account</span>
        <span className={accountStyle.profileText}>{user.nickname}</span>
      </div>
      <div className={accountStyle.belowDivider}>
        <div className={accountStyle.categoriesColumn}>
          {categories.map((name, id) => {
            return (
              <CategorySelector
                key={name}
                title={name}
                id={id}
                selected={category === id}
                callbackFunction={setCategory}
                Icon={getIcon(id)}
              />
            );
          })}
        </div>
        <div className={accountStyle.settingsColumn}>{getCategorySettings()}</div>
      </div>
    </div>
  );
}

export default Account;
